/**
 * Free plan quota. DESIGN.md §1 says 1 GB in the overview table but §8/§10.4
 * both settle on 2 GB when discussing the actual numbers — 2 GB is what the
 * scaffold task asked for, so that's the value wired in. Flagged in the
 * README under "Assumptions made during scaffold".
 */
export const FREE_QUOTA_BYTES = 2 * 1024 * 1024 * 1024; // 2 GiB
/**
 * Paid ("Pro") plan quota. DESIGN.md §10.4: ฿99/เดือน หรือ ฿990/ปี = 20 GB.
 * Applied to Archive.quotaBytes on a successful payment (see api payment/billing.ts).
 */
export const PRO_QUOTA_BYTES = 20 * 1024 * 1024 * 1024; // 20 GiB
/**
 * The paid plans (DESIGN.md §11). This value is the contract between three
 * places that must agree: the LINE checkout postback (`plan=`), the Stripe
 * session metadata, and the webhook that grants the plan — so it lives here in
 * shared, not in any one of them.
 *
 * Deliberately NOT a Stripe price id: prices live in Stripe (env
 * STRIPE_PRICE_*), durations live here, and this id is the stable join key
 * between them. Both plans are one-time top-ups that EXTEND planUntil rather
 * than auto-renewing subscriptions — DESIGN.md §11: PromptPay cannot auto-charge,
 * and the annual plan is explicitly "สแกนครั้งเดียวจบ".
 */
export const PLAN = { MONTHLY: "MONTHLY", ANNUAL: "ANNUAL" };
/**
 * How far each paid plan pushes Archive.planUntil forward, in *calendar* months
 * (added with Date.setMonth, not a fixed 30/365 days — so a payment on the 15th
 * lands on the 15th next period). DESIGN.md §10.4: monthly = 1 month, annual = 1 year.
 */
export const PLAN_DURATION_MONTHS = {
    MONTHLY: 1,
    ANNUAL: 12,
};
/**
 * Technical ceiling to keep a streaming worker (~1 GB RAM) alive — NOT a
 * product limit. DESIGN.md §3.1.1: "quota เป็นตัวคุมเอง... มีเพดานทางเทคนิค
 * (~1 GB) เพื่อกัน worker ตาย".
 */
export const MAX_INGEST_FILE_BYTES = 1 * 1024 * 1024 * 1024; // 1 GiB
/** Membership cache TTL — the window a removed member can still read the archive. DESIGN.md §5. */
export const MEMBERSHIP_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
/** Soft-deleted items purge after this long. DESIGN.md §7. */
export const TRASH_RETENTION_DAYS = 30;
/** Grace period after the bot is kicked from a group before hard delete. DESIGN.md §7. */
export const GROUP_FREEZE_RETENTION_DAYS = 30;
/**
 * Outer bound on how long ConsentLog / AuditLog rows are kept, counted from
 * when each row was written. PRIVACY.md §8 commits to this in writing: those
 * two tables normally die with their parent Archive (both are onDelete:
 * Cascade), but an archive that stays active forever would otherwise keep its
 * compliance logs forever — so the policy caps them at the general
 * prescription period under ป.พ.พ. (10 years) regardless.
 *
 * The number is the legal ceiling, not an operational preference: it is the
 * longest window in which those records could still be needed as evidence.
 * Shortening it is a legal decision, not a tuning knob — PRIVACY.md's table
 * has to change with it.
 *
 * Enforced by queue/jobs/purgeComplianceLogs.ts.
 */
export const COMPLIANCE_LOG_RETENTION_YEARS = 10;
/**
 * Bump this whenever TERMS.md / PRIVACY.md change in a way that requires
 * re-consent (CONSENT-MESSAGE.md checklist item: "เมื่อ TERMS/PRIVACY
 * เปลี่ยนเวอร์ชันสำคัญ ต้องขอยอมรับใหม่"). ConsentLog rows are stamped with
 * the version the user actually agreed to.
 */
export const CURRENT_TERMS_VERSION = "2026-07-15";
export const DEFAULT_PAGE_SIZE = 30;
export const MAX_PAGE_SIZE = 100;
//# sourceMappingURL=constants.js.map